export interface OcrSourceRegion {
  height: number;
  width: number;
  x: number;
  y: number;
}

// Tesseract needs roughly 30px+ character height; a value crop is upscaled towards the
// target dimension (capped so we do not invent detail), while full photos are downscaled.
const OCR_TARGET_DIMENSION = 1280;
const MAX_OCR_UPSCALE = 4;

export function createOcrSourceCanvas(image: HTMLImageElement, region?: OcrSourceRegion) {
  const sourceRegion = clampRegionToImage(
    region ?? { height: image.naturalHeight, width: image.naturalWidth, x: 0, y: 0 },
    image,
  );
  const scale = Math.min(MAX_OCR_UPSCALE, OCR_TARGET_DIMENSION / Math.max(sourceRegion.width, sourceRegion.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(sourceRegion.width * scale));
  canvas.height = Math.max(1, Math.round(sourceRegion.height * scale));

  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Unable to read photo for OCR.');
  }

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(
    image,
    sourceRegion.x,
    sourceRegion.y,
    sourceRegion.width,
    sourceRegion.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );

  return canvas;
}

export function createOcrCanvasVariants(source: HTMLCanvasElement) {
  const context = source.getContext('2d');

  if (!context) {
    return [source];
  }

  const imageData = context.getImageData(0, 0, source.width, source.height);
  const grayscale = readGrayscale(imageData);
  const threshold = computeOtsuThreshold(grayscale);

  return [
    source,
    // Dark print on a light nameplate.
    createBinarizedCanvas(source, grayscale, threshold, false),
    // Light digits on a dark LCD or roller counter; inverted so text ends up dark.
    createBinarizedCanvas(source, grayscale, threshold, true),
  ];
}

function clampRegionToImage(region: OcrSourceRegion, image: HTMLImageElement): OcrSourceRegion {
  const x = Math.min(Math.max(0, Math.round(region.x)), Math.max(0, image.naturalWidth - 1));
  const y = Math.min(Math.max(0, Math.round(region.y)), Math.max(0, image.naturalHeight - 1));

  return {
    height: Math.max(1, Math.min(Math.round(region.height), image.naturalHeight - y)),
    width: Math.max(1, Math.min(Math.round(region.width), image.naturalWidth - x)),
    x,
    y,
  };
}

function readGrayscale(imageData: ImageData) {
  const { data } = imageData;
  const grayscale = new Uint8ClampedArray(data.length / 4);

  for (let index = 0; index < grayscale.length; index += 1) {
    const offset = index * 4;
    grayscale[index] = Math.round(
      0.299 * (data[offset] ?? 0) + 0.587 * (data[offset + 1] ?? 0) + 0.114 * (data[offset + 2] ?? 0),
    );
  }

  return grayscale;
}

function computeOtsuThreshold(grayscale: Uint8ClampedArray) {
  const histogram = new Array<number>(256).fill(0);

  for (const value of grayscale) {
    histogram[value] = (histogram[value] ?? 0) + 1;
  }

  const total = grayscale.length;
  let sum = 0;

  for (let value = 0; value < 256; value += 1) {
    sum += value * (histogram[value] ?? 0);
  }

  let sumBackground = 0;
  let weightBackground = 0;
  let bestVariance = 0;
  let bestThreshold = 127;

  for (let value = 0; value < 256; value += 1) {
    const count = histogram[value] ?? 0;
    weightBackground += count;

    if (weightBackground === 0) {
      continue;
    }

    const weightForeground = total - weightBackground;

    if (weightForeground === 0) {
      break;
    }

    sumBackground += value * count;

    const meanBackground = sumBackground / weightBackground;
    const meanForeground = (sum - sumBackground) / weightForeground;
    const variance = weightBackground * weightForeground * (meanBackground - meanForeground) ** 2;

    if (variance > bestVariance) {
      bestVariance = variance;
      bestThreshold = value;
    }
  }

  return bestThreshold;
}

function createBinarizedCanvas(
  source: HTMLCanvasElement,
  grayscale: Uint8ClampedArray,
  threshold: number,
  inverted: boolean,
) {
  const canvas = document.createElement('canvas');
  canvas.width = source.width;
  canvas.height = source.height;

  const context = canvas.getContext('2d');

  if (!context) {
    return source;
  }

  const imageData = context.createImageData(canvas.width, canvas.height);
  const { data } = imageData;

  for (let index = 0; index < grayscale.length; index += 1) {
    const isLight = (grayscale[index] ?? 0) > threshold;
    const value = (inverted ? !isLight : isLight) ? 255 : 0;
    const offset = index * 4;
    data[offset] = value;
    data[offset + 1] = value;
    data[offset + 2] = value;
    data[offset + 3] = 255;
  }

  context.putImageData(imageData, 0, 0);

  return canvas;
}
